import { joinURL, withHttp, withQuery } from 'ufo';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useMerchantUrlUtils } from '~/composables/useMerchantUrlUtils';

/**
 * Middleware that redirects merchants with custom URLs (not served via appBaseUrl)
 * to URLs where the merchantSlug is replaced with 'c'.
 * Example redirects:
 * - https://affiliate.bauhaus.info/bauhaus/ → https://affiliate.bauhaus.info/c/
 * - https://app.clickfire.io/bauhaus → https://affiliate.bauhaus.info/c/
 */
export default defineNuxtRouteMiddleware(async (to) => {
	const merchantSlug = useMerchantSlug(to);
	const url = useRequestURL();
	const computedOrigin = joinURL(url.origin, to.fullPath);

	// Skip middleware if no merchant slug is present
	if (!merchantSlug.value) {
		return;
	}

	const {
		isStaticMerchantUrl,
		hasStaticMerchantSlug,
		getRouteWithStaticSlug,
		hasMerchantStaticUrl,
		isOnCorrectMerchantUrl,
		getStaticMerchantUrl,
	} = useMerchantUrlUtils();

	// Redirect to static slug URL if on merchant URL but without static slug
	if (isStaticMerchantUrl(computedOrigin) && !hasStaticMerchantSlug(computedOrigin)) {
		return navigateTo(getRouteWithStaticSlug(to));
	}

	if (hasMerchantStaticUrl(merchantSlug.value) && !isOnCorrectMerchantUrl(merchantSlug.value, computedOrigin)) {
		const correctMerchantUrl = getStaticMerchantUrl(merchantSlug.value) as string;
		const routeWithStaticSlug = getRouteWithStaticSlug(to);
		return navigateTo(withQuery(withHttp(joinURL(correctMerchantUrl, routeWithStaticSlug.href)), { redirectFromAnotherSpace: true }), { external: true });
	}
});
